import { base, type IIconProps } from './base';

/* ---------- Dashboard / Command-Center card glyphs ----------
   Added when the new /dashboard cards landed; previously redefined
   inline in each card. Stroke-only via the shared `base` props unless
   the glyph is intrinsically filled (e.g. `SparkleIcon`'s sibling
   `PaperPlaneFilledIcon`). All variants pick up colour from
   the wrapping `currentColor`. */

export function EyeIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Eye crossed by a diagonal slash — the "hidden" counterpart to EyeIcon.
    Same 14px default so the two swap in place without shifting a row. */
export function EyeOffIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="m3 3 18 18" />
      <path d="M10.6 6.1A9.6 9.6 0 0 1 12 6c6.5 0 10 6 10 6a17 17 0 0 1-3.1 3.9" />
      <path d="M6.3 8.2A17 17 0 0 0 2 12s3.5 6 10 6a9.4 9.4 0 0 0 3.4-.6" />
    </svg>
  );
}

export function TargetIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

export function TrendUpIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}

export function TrendDownIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
      <polyline points="16 17 22 17 22 11" />
    </svg>
  );
}

export function WavesIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
    </svg>
  );
}

export function SparkleIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
      <path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" />
    </svg>
  );
}

export function CloudIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M17.5 19a4.5 4.5 0 0 0 1-8.86A6 6 0 0 0 6.32 11.5 4 4 0 0 0 7 19z" />
    </svg>
  );
}

export function SunIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2"  x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93"  x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34"  x2="19.07" y2="4.93" />
    </svg>
  );
}

export function ThermometerIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4 4 0 1 0 5 0z" />
    </svg>
  );
}

export function DropletIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  );
}

export function WindIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M9.59 4.59A2 2 0 1 1 11 8H2" />
      <path d="M12.59 19.41A2 2 0 1 0 14 16H2" />
      <path d="M17.73 7.73A2.5 2.5 0 1 1 19.5 12H2" />
    </svg>
  );
}

export function InfoCircleIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8"  x2="12" y2="8.01" />
      <line x1="12" y1="12" x2="12" y2="16" />
    </svg>
  );
}

/** Filled ⓘ-style alert disc with the "!" cut out, so it needs no second
    colour — the safety alert glyph. Fills with `currentColor`. */
export function AlertCircleFilledIcon({ size = 16 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM8 3.5a1 1 0 0 0-1 1V8a1 1 0 0 0 2 0V4.5a1 1 0 0 0-1-1ZM8 10.5a1 1 0 1 0 0 2a1 1 0 0 0 0-2Z"
      />
    </svg>
  );
}

/** Red ⊘ glyph for the Workforce "Missing" stat — matches Lovable's
    circle-with-slash icon. Stroke-only; the wrapping span tints. */
export function ProhibitedIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}

export function FlameIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 17c0 1.5-2.5 2-2.5 4 1 0 6-1 6-7.5 0-2-1.5-3.5-2.5-5C11 6 11 4 11 2c-3 1-7 6-7 10.5a5 5 0 0 0 4.5 5" />
    </svg>
  );
}

export function TruckIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <rect x="1" y="6" width="14" height="11" rx="1" />
      <path d="M15 9h4l3 3v5h-7z" />
      <circle cx="5.5" cy="18.5" r="1.5" />
      <circle cx="18.5" cy="18.5" r="1.5" />
    </svg>
  );
}

export function FileTextIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="14" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </svg>
  );
}

/* Inspection-row status glyphs used by the Quality Assurance card.
   Stroke inherits via `currentColor`; the wrapping span picks the
   colour via a per-status CSS class. */
export function StatusPassIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12 11 15 16 9" />
    </svg>
  );
}

export function StatusFailIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
      <line x1="9"  y1="9"  x2="15" y2="15" />
      <line x1="15" y1="9"  x2="9"  y2="15" />
    </svg>
  );
}

export function StatusPendingIcon({ size = 18 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

/* Send-arrow glyph used by the AI Agent input — filled triangle,
   `currentColor` so the surrounding button's text colour drives the
   tint. Distinct from the existing stroke-based SendIcon. */
export function PaperPlaneFilledIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 20l18-8L3 4l4 8z" />
    </svg>
  );
}

/* Hollow / dotted phase markers used by Project Progress (off vs
   half-done states). Filled-check phase variant reuses CheckCircleIcon. */
export function HollowCircleIcon({ size = 16 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

export function DotCircleIcon({ size = 16 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="3" fill="currentColor" />
    </svg>
  );
}

export function PencilIcon({ size = 14 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...base}>
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

/**
 * Small status dot used inside chips/pills (e.g. portfolio status badges).
 * Solid filled circle in `currentColor` so the chip's text colour cascades.
 */
export function StatusDotIcon({ size = 8 }: IIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 8 8" aria-hidden>
      <circle cx="4" cy="4" r="4" fill="currentColor" />
    </svg>
  );
}
